import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { Button } from '../../../interaxDesignSystemComponents'
import { PencilIcon } from '@heroicons/react/24/outline'
import { GamerFeedbackTabBar } from '../../../interaxDesignSystemComponents'
import { extractFeedbackText } from '../helpers/feedbackTextExtractor'
import {
  getSectionCardContainerStyles,
  getTabBarContainerStyles,
  getContentLayoutStyles
} from '../helpers/sectionCardStyles'
import { SectionHeader } from './SectionHeader'
import { TextPanel, VisualizationPanel } from './ContentPanels'
import { useGlowIntersection } from '../../gamer/hooks/useGlowIntersection'
import type { VisualizationRendererProps } from './VisualizationRenderer'
import type { LoopCommunicationData, PsychologicalSafetyQuestions } from '../../../types/TeamSwimmerFeedbackTypes'
import type { TeamPerformanceData } from '../../facilitator/components/GenerateReport/visualizations/teamInformationShares/types'
import type { DraftJsContent } from '../../../../../pure-js/libs/Common'
import { ContentState, EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import DOMPurify from 'dompurify'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

// Type for llmAnalysis - can be DraftJsContent (new format) or string (legacy)
export type LlmAnalysisContent = DraftJsContent | string

export interface SectionDataEntry {
  visualization?: VisualizationRendererProps['visualization']
  variable?: string
  context?: 'strengths' | 'weaknesses'
  loops?: LoopCommunicationData[]
  chartData?: TeamPerformanceData[] // For line_chart visualization
  dominantEmotionClass?: string
  teamCommunicatedEmotionClass?: string
  questions?: Record<string, number> | PsychologicalSafetyQuestions
  llmAnalysis?: LlmAnalysisContent // For report data - DraftJsContent or legacy string
  // Team data info for team selector
  teamData?: Array<{
    teamId: string
    teamName: string
  }>
  // Current selected team IDs (array of selected team IDs, empty means no teams selected)
  selectedTeamIds?: string[]
  // Callback to update selected teams
  onTeamChange?: (selectedTeamIds: string[]) => void
}

export type SectionDataRecord = Record<string, SectionDataEntry>

export interface SectionTab {
  label: string
  key: string
}

export interface ExperimentalSectionCardProps {
  /**
   * Translation key for title
   */
  titleKey: string
  /**
   * Translation key for description (optional)
   */
  descriptionKey?: string
  /**
   * Tabs for the section
   */
  tabs: SectionTab[]
  /**
   * Section data containing tab content
   */
  sectionData: SectionDataRecord
  /**
   * Variant for team or individual styling
   */
  variant: 'team' | 'individual'
  /**
   * Optional icon component
   */
  icon?: React.ReactNode
  /**
   * Controls first-visit glow experience overlay visibility
   */
  showGlowExperience?: boolean
  /**
   * Callback fired when the glow overlay is dismissed
   */
  onGlowExperienceComplete?: () => void
  /**
   * Callback fired when the glint animation state changes
   */
  onGlintStateChange?: (state: 'idle' | 'running') => void
  /**
   * Controls whether the section content is editable
   * Set to true for generate report flow, false for other uses
   * @default false
   */
  isEditable?: boolean
  /**
   * Callback fired when content is edited and saved
   * @param tabKey - The key of the tab being edited
   * @param content - The new HTML content
   */
  onContentChange?: (tabKey: string, content: string) => void
  /**
   * When false, hides the inner section title row (e.g. when a parent shell or step intro already shows the same title).
   * Tabs and content are unchanged.
   * @default true
   */
  showSectionHeader?: boolean
}

/**
 * ExperimentalSectionCard - Section card with tabs, text content, and visualization
 *
 * Features:
 * - Variant-aware background (team vs individual)
 * - i18n support (translation keys)
 * - Optional header icon
 * - Tab navigation
 * - Two-column layout (left: text, right: visualization)
 * - Uses VisualizationRenderer for right side
 *
 * This component has been refactored into smaller pieces:
 * - SectionHeader: Header with title, description, and icon
 * - TextPanel: Left panel with text content
 * - VisualizationPanel: Right panel with visualization
 * - feedbackTextExtractor: Helper for extracting feedback text
 * - sectionCardStyles: Helper for styling
 */
const sanitizedHtml = (html: string) => DOMPurify.sanitize(html || '')

const createEditorStateFromHtml = (html: string) => {
  const sanitized = sanitizedHtml(html)
  if (!sanitized) {
    return EditorState.createEmpty()
  }
  const blocksFromHtml = htmlToDraft(sanitized)
  if (!blocksFromHtml) {
    return EditorState.createEmpty()
  }
  const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap)
  return EditorState.createWithContent(contentState)
}

// DraftEditor is used in ContentPanels.tsx, not here

export const ExperimentalSectionCard: React.FC<ExperimentalSectionCardProps> = ({
  titleKey,
  descriptionKey,
  tabs,
  sectionData,
  variant,
  icon,
  showGlowExperience = false,
  onGlowExperienceComplete,
  onGlintStateChange,
  isEditable = false,
  onContentChange,
  showSectionHeader = true
}) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(tabs[0]?.key || '')
  const hasRevealedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const sectionIdRef = useRef<string | null>(activeTab)
  const [glintTrigger, setGlintTrigger] = useState(0)
  const [isGlintRunning, setIsGlintRunning] = useState(false)
  const [editingTab, setEditingTab] = useState<string | null>(null)
  const [editorState, setEditorState] = useState<EditorState | null>(null)

  const activeTabData = sectionData[activeTab]

  // Get the active tab label for display
  const activeTabLabel = tabs.find((tab) => tab.key === activeTab)?.label || activeTab

  // Extract feedback text using helper function (must be before early return)
  const feedbackText = useMemo(
    () => (activeTabData ? extractFeedbackText(activeTabData, activeTab, variant, titleKey, t) : ''),
    [activeTabData, activeTab, variant, titleKey, t]
  )

  // Initialize editor state when entering edit mode
  useEffect(() => {
    if (isEditable && editingTab === activeTab && !editorState) {
      setEditorState(createEditorStateFromHtml(feedbackText))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, editingTab, activeTab, feedbackText])

  // Reset editor state when switching tabs while editing
  useEffect(() => {
    if (editingTab && editingTab !== activeTab) {
      setEditingTab(null)
      setEditorState(null)
    }
  }, [activeTab, editingTab])

  const handleEdit = useCallback(() => {
    setEditorState(createEditorStateFromHtml(feedbackText))
    setEditingTab(activeTab)
  }, [feedbackText, activeTab])

  const handleSave = useCallback(() => {
    if (!editorState || !onContentChange) return
    const raw = convertToRaw(editorState.getCurrentContent())
    const html = sanitizedHtml(draftToHtml(raw))
    onContentChange(activeTab, html)
    setEditingTab(null)
    setEditorState(null)
  }, [editorState, onContentChange, activeTab])

  const handleCancel = useCallback(() => {
    setEditorState(null)
    setEditingTab(null)
  }, [])

  // Update activeTab when tabs or sectionData change to ensure it's always valid
  useEffect(() => {
    if (tabs.length > 0) {
      // If current activeTab doesn't exist in sectionData, switch to first available tab
      if (!activeTab || !sectionData[activeTab]) {
        const firstAvailableTab = tabs.find((tab) => sectionData[tab.key])?.key || tabs[0]?.key
        if (firstAvailableTab && firstAvailableTab !== activeTab) {
          setActiveTab(firstAvailableTab)
        }
      }
    }
  }, [tabs, sectionData]) // Removed activeTab from deps to prevent infinite loops

  // Get translated header text
  const title = t(titleKey)
  const description = descriptionKey ? t(descriptionKey) : undefined

  const handleGlintStateChange = useCallback(
    (state: 'idle' | 'running') => {
      setIsGlintRunning(state === 'running')
      onGlintStateChange?.(state)
    },
    [onGlintStateChange]
  )

  useEffect(() => {
    if (!showGlowExperience) {
      hasRevealedRef.current = false
      sectionIdRef.current = activeTab
      setGlintTrigger(0)
      return
    }

    if (sectionIdRef.current !== activeTab) {
      hasRevealedRef.current = false
      sectionIdRef.current = activeTab
      setGlintTrigger((value) => value + 1)
    }
  }, [activeTab, showGlowExperience])

  useGlowIntersection(containerRef, {
    enabled: showGlowExperience,
    onEnter: () => {
      if (!hasRevealedRef.current && !isGlintRunning) {
        setGlintTrigger((value) => value + 1)
      }
    }
  })

  // Allow rendering even without visualization if we have text content (for report data)
  // Don't return null if activeTabData is missing - let useEffect handle tab switching
  if (!activeTabData) {
    // Return early but useEffect will fix the activeTab
    return null
  }

  // Extract data with safe defaults
  const {
    visualization,
    variable,
    context,
    loops,
    chartData,
    dominantEmotionClass,
    teamCommunicatedEmotionClass,
    questions,
    teamData,
    selectedTeamIds,
    onTeamChange
  } = activeTabData
  return (
    <Box
      ref={containerRef}
      sx={getSectionCardContainerStyles(variant)}
      onMouseEnter={() => {
        if (showGlowExperience && !hasRevealedRef.current && !isGlintRunning) {
          setGlintTrigger((value) => value + 1)
        }
      }}
      onFocus={() => {
        if (showGlowExperience && !hasRevealedRef.current && !isGlintRunning) {
          setGlintTrigger((value) => value + 1)
        }
      }}
    >
      {/* Header — optional when parent already provides the section title */}
      {showSectionHeader && <SectionHeader title={title} description={description} icon={icon} />}

      {/* Tab Bar */}
      <Box sx={getTabBarContainerStyles()}>
        <GamerFeedbackTabBar
          tabs={tabs.map((t) => ({ label: t.label, value: t.key }))}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      </Box>

      {/* Content - Responsive: Column on mobile, Row on desktop */}
      <Box sx={getContentLayoutStyles()}>
        {/* Left: Text Content */}
        <Stack spacing={2} sx={{ flex: 1, width: '100%' }}>
          <TextPanel
            label={activeTabLabel}
            content={feedbackText}
            isEditing={editingTab === activeTab}
            editorState={editorState}
            onEditorStateChange={setEditorState}
            onSave={handleSave}
            onCancel={handleCancel}
          />
          {isEditable && editingTab !== activeTab && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button
                variant="secondary-gold"
                onClick={handleEdit}
                icon={<PencilIcon className="w-4 h-4" />}
                iconPosition="left"
              >
                {t('facilitator.reportFlow.editButton')}
              </Button>
            </Box>
          )}
        </Stack>

        {/* Right: Visualization */}
        {visualization && (
          <VisualizationPanel
            visualization={visualization}
            metricName={activeTab}
            variableName={variable}
            context={context}
            variant={variant}
            additionalData={{
              loops,
              chartData,
              emotion: variant === 'individual' ? dominantEmotionClass : teamCommunicatedEmotionClass,
              questions
            }}
            teamData={teamData}
            selectedTeamIds={selectedTeamIds}
            onTeamChange={onTeamChange}
            glintTrigger={glintTrigger}
            showGlowExperience={showGlowExperience}
            onGlowExperienceComplete={() => {
              if (!hasRevealedRef.current) {
                hasRevealedRef.current = true
                onGlowExperienceComplete?.()
              }
            }}
            onGlintStateChange={handleGlintStateChange}
          />
        )}
      </Box>
    </Box>
  )
}

export default ExperimentalSectionCard
